import topApi from '../../components/api/ToPApi';
import { downloadCsvText } from '../../utils/csv';

// Importación / exportación de productos en CSV (la plantilla, las validaciones y el CSV los arma el API)
export type ImportType = 'products' | 'inventory';

export const IMPORT_LABELS: Record<ImportType, { title: string; short: string; description: string }> = {
    products: {
        title: 'Importar productos',
        short: 'Productos',
        description: 'Crea o actualiza productos del menú: nombre, precio, menú, unidad e impuestos.',
    },
    inventory: {
        title: 'Importar productos con inventario',
        short: 'Productos con inventario',
        description: 'Además de los datos del producto, crea o actualiza su inventario: stock, costo, proveedor y categoría.',
    },
};

// Tamaño máximo del archivo (5000 filas caben de sobra)
export const MAX_FILE_MB = 5;

export interface ImportColumn {
    key: string;
    required: boolean;
    description: string;
    example: string;
}

export interface ImportTemplate {
    csv: string;
    filename: string;
    columns: ImportColumn[];
    units: { id: string; description: string }[];
    taxCodes: { id: string; description: string }[];
}

export type RowAction = 'create' | 'update' | 'unchanged' | 'error';

export interface ImportRowResult {
    line: number;
    code: string;
    name: string;
    menu: string;
    action: RowAction;
    errors: string[];
    notes: string[];
}

export interface ImportResult {
    imported: boolean;
    fileErrors: string[];
    warnings: string[];
    summary: { total: number; create: number; update: number; unchanged: number; errors: number };
    rows: ImportRowResult[];
}

const errorMessage = (e: any) => e?.response?.data?.error || 'Ocurrió un error de comunicación, intente de nuevo.';

export const loadImportTemplate = async (type: ImportType): Promise<ImportTemplate> => {
    try {
        const resp = await topApi.get<ImportTemplate>('/product/import/template', { params: { type } });
        return resp.data;
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export const downloadTemplate = (template: ImportTemplate) => downloadCsvText(template.filename, template.csv);

export const importProducts = async (type: ImportType, csv: string, dryRun: boolean): Promise<ImportResult> => {
    try {
        const resp = await topApi.post<ImportResult>('/product/import', { type, csv, dryRun });
        return resp.data;
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

/** Descarga los productos con la misma estructura de la plantilla de importación. Devuelve cuántos se exportaron. */
export const exportProducts = async (type: ImportType) => {
    try {
        const resp = await topApi.get<{ csv: string; filename: string; total: number; skipped: number }>('/product/export', { params: { type } });
        if (resp.data.total > 0) downloadCsvText(resp.data.filename, resp.data.csv);
        return { total: resp.data.total, skipped: resp.data.skipped ?? 0 };
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

/**
 * Lee el archivo como texto. Excel guarda los CSV en UTF-8 o en Windows-1252 según la opción elegida:
 * si no es UTF-8 válido se lee como Windows-1252 para conservar tildes y eñes.
 */
export const readCsvFile = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
        return new TextDecoder('windows-1252').decode(buffer);
    }
};

export const validateCsvFile = (file: File): string | null => {
    if (!/\.csv$/i.test(file.name)) {
        return 'El archivo debe ser .csv. Si lo tiene en Excel, use "Guardar como" > "CSV UTF-8 (delimitado por comas)".';
    }
    if (file.size === 0) return 'El archivo está vacío.';
    if (file.size > MAX_FILE_MB * 1024 * 1024) return `El archivo supera los ${MAX_FILE_MB} MB. Divídalo en varios archivos.`;
    return null;
};
